import { useStyle } from '@/classify';
import { Magnifier } from '@/components/ImageMagnifiers';
import { useProductImageCarousel } from '@/hooks/ProductImageCarousel/useProductImageCarousel';
import useIsMobile from '@/hooks/use-is-mobile';
import clsx from 'clsx';
import Image from 'next/image';
import React, { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { MOUSE_ACTIVATION } from 'react-input-position';
import ReactPlayer from 'react-player';
import Slider from 'react-slick';
import defaultClasses from './carousel.module.css';
import MobileVideoPreview from './mobile-video-preview';
import Thumbnail from './thumbnail';
import { AspectRatio } from '../ui/aspect-ratio';

const IMAGE_WIDTH = 692;

const ImagesViewerModal = React.lazy(() => import('@/components/ReviewList/imagesViewerModal'));

const ProductImageCarousel = props => {
  const {
    images,
    videoUrl,
    setCurrentIndex,
    mark,
    sku,
    name,
    version,
    categoryName,
    fabricLabel,
    colorLabel,
    isImageGallery = false,
    showSpecialImage,
    shareWith,
  } = props;

  const generateFile = ({ item, version }) => {
    const image =
      item.file.indexOf('http') > -1
        ? version
          ? `${item.file}?version=${version}`
          : item.file
        : process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL_IMAGE +
          'media/catalog/product' +
          (item.file[0] === '/'
            ? version
              ? `${item.file}?version=${version}`
              : item.file
            : version
              ? `/${item.file}?version=${version}`
              : `/${item.file}`);

    return image;
  };

  const isMobile = useIsMobile();

  const classes = useStyle(defaultClasses, props.classes);

  const [productImages, setProductImages] = useState([]);

  const imageViewerRef = useRef(null);

  const mobileScrollContainerRef = useRef(null);

  const talonProps = useProductImageCarousel({
    images,
    imageWidth: IMAGE_WIDTH,
    setCurrentIndex,
  });

  const {
    currentImage,
    activeItemIndex,
    handleThumbnailClick,
    sortedImages,
    currentProductImageIndex,
    setCurrentProductImageIndex,
    showVideo,
    setShowVideo,
  } = talonProps;

  const firstImageUrl = (() => {
    if (!sortedImages.length) {
      return '';
    }

    const file = sortedImages[0].file;

    return version && file ? `${file}?version=${version}` : file;
  })();

  const thumbnails = useMemo(
    () =>
      sortedImages.reduce((acc, item, index, arr) => {
        if (index === arr.length - 1) {
          acc.push(
            <div key={index}>
              <Thumbnail
                alt={generateImageAltText({
                  name,
                  index,
                  colorLabel,
                  fabricLabel,
                  categoryName,
                })}
                item={item}
                itemIndex={index}
                isActive={showVideo ? false : activeItemIndex === index}
                onClickHandler={handleThumbnailClick}
                version={version}
                sku={sku}
                showSpecialImage={showSpecialImage}
                isSpecialImage={validateSpecialImage(item)}
              />
            </div>,
          );

          if (videoUrl) {
            acc.push(
              <div key={index + 1}>
                <div
                  className={clsx(
                    'relative h-[162px] w-[116px] p-2 lg_w-auto',
                    ' cursor-pointer',
                    'flex items-center justify-center',
                    showVideo &&
                      'before_pointer-events-none before_absolute before_h-full before_w-full before_border-2 before_border-solid before_border-theme before_content-[""] ',
                  )}
                  onClick={() => {
                    setShowVideo(true);
                  }}
                >
                  <img
                    alt='video'
                    src={generateYoutubeVideoPosterUrl(videoUrl, firstImageUrl)}
                    data-try-default='1'
                    onError={e => {
                      const eventTarget = e.target;
                      const tryDefault = eventTarget.dataset.tryDefault;

                      if (!tryDefault) {
                        return;
                      }

                      eventTarget.removeAttribute('data-try-default');

                      e.target.src = firstImageUrl;
                    }}
                    className='h-full w-full object-cover'
                  />
                  <div className='absolute left-1/2 top-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2  flex-col items-center justify-center bg-black/30 bg-clip-content p-2'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='50'
                      height='51'
                      fill='none'
                      viewBox='0 0 50 51'
                    >
                      <g
                        stroke='#fff'
                        clipPath='url(#clip0_11911_35405)'
                      >
                        <path
                          strokeWidth='2'
                          d='M25 49.5c13.255 0 24-10.745 24-24s-10.745-24-24-24-24 10.745-24 24 10.745 24 24 24z'
                        />
                        <path
                          fill='#fff'
                          d='M33.75 24.202c1 .577 1 2.02 0 2.598l-12 6.928a1.5 1.5 0 01-2.25-1.3V18.574a1.5 1.5 0 012.25-1.3l12 6.929z'
                        />
                      </g>
                      <defs>
                        <clipPath id='clip0_11911_35405'>
                          <path
                            fill='#fff'
                            d='M0 0H50V50H0z'
                            transform='translate(0 .5)'
                          />
                        </clipPath>
                      </defs>
                    </svg>
                    <span className='mt-[5px] text-sm text-white'>Video</span>
                  </div>
                </div>
              </div>,
            );
          }

          return acc;
        }

        acc.push(
          <div key={index}>
            <Thumbnail
              alt={generateImageAltText({
                name,
                index,
                colorLabel,
                fabricLabel,
                categoryName,
              })}
              item={item}
              itemIndex={index}
              isActive={showVideo ? false : activeItemIndex === index}
              onClickHandler={handleThumbnailClick}
              version={version}
              sku={sku}
            />
          </div>,
        );

        return acc;
      }, []),
    [
      activeItemIndex,
      handleThumbnailClick,
      name,
      sortedImages,
      videoUrl,
      version,
      colorLabel,
      fabricLabel,
      categoryName,
      firstImageUrl,
      showVideo,
      setShowVideo,
      sku,
      showSpecialImage,
    ],
  );

  const bigImageAltText = generateImageAltText({
    name,
    index: activeItemIndex,
    colorLabel,
    fabricLabel,
    categoryName,
  });

  const onImageClick = useCallback(
    index => {
      const converted = images.map(item => {
        if (item.file.indexOf('http') > -1) {
          return {
            src: version ? `${item.file}?version=${version}` : item.file,
          };
        }

        return {
          src:
            process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL_IMAGE +
            'media/catalog/product' +
            (item.file[0] === '/'
              ? version
                ? `${item.file}?version=${version}`
                : item.file
              : version
                ? `/${item.file}?version=${version}`
                : `/${item.file}`),
        };
      });

      setProductImages(converted);
      setCurrentProductImageIndex(index);
      imageViewerRef?.current.setIsOpen(true);
    },
    [images, setCurrentProductImageIndex, version],
  );

  const magnifier = useMemo(() => {
    if (!currentImage.file) {
      return null;
    }

    const imageUrl =
      currentImage.file.indexOf('http') > -1
        ? version
          ? `${currentImage.file}?version=${version}`
          : currentImage.file
        : process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL_IMAGE +
          'media/catalog/product' +
          (currentImage.file[0] === '/'
            ? version
              ? `${currentImage.file}?version=${version}`
              : currentImage.file
            : version
              ? `/${currentImage.file}?version=${version}`
              : `/${currentImage.file}`);

    return (
      <>
        <Magnifier
          className='input-position'
          imageSrc={imageUrl}
          largeImageSrc={imageUrl}
          allowOverflow
          mouseActivation={MOUSE_ACTIVATION.HOVER}
          dragToMove={false}
          imageAlt={bigImageAltText}
        />
      </>
    );
  }, [currentImage.file, version, bigImageAltText]);

  const maybeNewMark =
    mark && mark.indexOf('new') > -1 ? (
      <div className='absolute -left-[3px] -top-0.5 z-10 lg_-left-1 lg_-top-[3px] '>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='67'
          height='40'
          fill='none'
          viewBox='0 0 67 40'
          className='h-auto w-[53px] lg_w-[67px]'
        >
          <g clipPath='url(#clip0_13125_57333)'>
            <path
              fill='#80583F'
              d='M0 35.395h64.075a2.174 2.174 0 0 0 2.175-2.171V2.17A2.174 2.174 0 0 0 64.075 0h-61.9A2.174 2.174 0 0 0 0 2.171zM2.175 35.398H0v2.171c0-1.198.974-2.17 2.175-2.17'
            />
            <path
              fill='#80583F'
              d='M3.823 35.398v4.342H2.175A2.174 2.174 0 0 1 0 37.57c0-1.2.974-2.172 2.175-2.172z'
            />
            <path
              fill='#fff'
              d='M27.987 11.75h1.275v11.025l-7.95-8.34v7.815h-1.275V11.225l7.95 8.34zm6.276 10.65q-.96 0-1.71-.45a3.24 3.24 0 0 1-1.155-1.275q-.405-.81-.405-1.875 0-1.08.42-1.89.435-.81 1.2-1.26.78-.45 1.8-.45t1.74.435q.735.42 1.125 1.215t.39 1.89q0 .105-.015.225v.135h-5.895v-1.02h4.95l-.48.69q.045-.075.09-.225.045-.165.045-.27 0-.615-.255-1.065-.24-.45-.675-.705a2 2 0 0 0-1.02-.255q-.69 0-1.185.3-.48.285-.75.84-.255.54-.27 1.335 0 .81.255 1.38t.75.87 1.2.3 1.245-.3q.54-.315.915-.945l.975.615q-.54.87-1.365 1.32-.81.435-1.92.435m3.78-7.05h1.35l1.725 4.485 1.875-5.01 2.01 5.04 1.74-4.515h1.35l-3 7.425-2.085-5.025-1.965 5.025z'
            />
          </g>
          <defs>
            <clipPath id='clip0_13125_57333'>
              <path
                fill='#fff'
                d='M0 0h66.25v40H0z'
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    ) : null;

  const maybeBestSellerMark =
    mark && mark.indexOf('best seller') > -1 ? (
      <div className='absolute -left-[3px] -top-0.5 z-10 lg_-left-1 lg_-top-[3px] '>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='104'
          height='40'
          fill='none'
          viewBox='0 0 104 40'
          className='h-auto w-[83px] lg_w-[104px]'
        >
          <g clipPath='url(#clip0_13126_55493)'>
            <path
              fill='#80583F'
              d='M0 35.395h101.82c1.203 0 2.18-.973 2.18-2.171V2.17A2.176 2.176 0 0 0 101.82 0H2.18C.977 0 0 .972 0 2.171zM2.18 35.398H0v2.171c0-1.198.977-2.17 2.18-2.17'
            />
            <path
              fill='#80583F'
              d='M3.832 35.398v4.342H2.18A2.176 2.176 0 0 1 0 37.57c0-1.2.977-2.172 2.18-2.172z'
            />
            <path
              fill='#fff'
              d='M20.267 16.7v-.705h1.695q.525 0 .945-.18t.66-.525.24-.84q0-.75-.525-1.155-.525-.42-1.32-.42h-1.425v8.25h1.575q.675 0 1.185-.24.525-.24.81-.69.3-.45.3-1.095 0-.48-.166-.855-.165-.375-.48-.63-.3-.255-.72-.375-.42-.135-.93-.135h-1.844V16.4h1.845q.765 0 1.41.165t1.125.51q.495.33.765.855t.27 1.245q0 1.035-.48 1.725-.465.675-1.276 1.02-.795.33-1.814.33h-2.85v-10.5h2.7q.945 0 1.634.285.705.285 1.096.87.39.57.39 1.47 0 .735-.39 1.26t-1.096.795q-.69.27-1.634.27zm9.954 5.7q-.96 0-1.71-.45a3.24 3.24 0 0 1-1.155-1.275q-.405-.81-.405-1.875 0-1.08.42-1.89.435-.81 1.2-1.26.78-.45 1.8-.45t1.74.435q.735.42 1.125 1.215t.39 1.89q0 .105-.015.225v.135h-5.895v-1.02h4.95l-.48.69q.045-.075.09-.225.045-.165.045-.27 0-.615-.255-1.065-.24-.45-.675-.705a2 2 0 0 0-1.02-.255q-.69 0-1.185.3-.48.285-.75.84-.255.54-.27 1.335 0 .81.255 1.38t.75.87 1.2.3 1.245-.3q.54-.315.915-.945l.975.615q-.54.87-1.365 1.32-.81.435-1.92.435m5.335-2.265q.21.315.48.585t.614.435q.36.15.78.15.51 0 .795-.225.3-.24.3-.645 0-.36-.24-.6-.224-.255-.584-.42-.36-.18-.766-.33-.45-.18-.915-.42-.45-.255-.765-.645-.3-.405-.3-1.005 0-.615.316-1.02.33-.405.84-.6a3.08 3.08 0 0 1 2.985.465q.375.3.585.69l-.96.615q-.285-.39-.72-.645-.42-.255-.96-.255-.375 0-.645.165t-.27.51q0 .27.21.48t.54.375.69.315q.6.24 1.094.525.495.27.78.66.3.39.3 1.005 0 .9-.66 1.5-.645.6-1.71.6-.69 0-1.244-.24a3.2 3.2 0 0 1-.945-.645q-.375-.39-.57-.795zm4.714-4.785h3.45v1.125h-3.45zm1.125-2.4h1.2v9.3h-1.2zm8.49 6.12q.36.63.75 1.11.405.48.9.75t1.126.27q.84 0 1.35-.465t.51-1.185q0-.705-.33-1.125a2.4 2.4 0 0 0-.87-.675q-.525-.27-1.125-.48-.39-.135-.87-.345a5 5 0 0 1-.915-.555 2.9 2.9 0 0 1-.72-.84q-.27-.495-.27-1.215 0-.78.39-1.395t1.08-.96q.69-.36 1.575-.36.855 0 1.485.33.645.315 1.095.825.45.495.705 1.05l-1.095.63a4.3 4.3 0 0 0-.495-.765q-.3-.375-.735-.615-.42-.24-1.035-.24-.855 0-1.245.42-.39.405-.39.915 0 .435.225.81.225.36.75.69.54.315 1.455.63.42.15.885.39t.855.615q.405.36.66.885.255.51.255 1.23 0 .69-.27 1.245-.255.555-.72.945-.45.39-1.035.6-.57.21-1.2.21-.855 0-1.59-.36a4.7 4.7 0 0 1-1.275-.975 6.1 6.1 0 0 1-.885-1.305zM60.31 22.4q-.96 0-1.71-.45a3.24 3.24 0 0 1-1.155-1.275q-.405-.81-.405-1.875 0-1.08.42-1.89.435-.81 1.2-1.26.78-.45 1.8-.45t1.74.435q.735.42 1.125 1.215t.39 1.89q0 .105-.015.225v.135h-5.895v-1.02h4.95l-.48.69q.045-.075.09-.225.045-.165.045-.27 0-.615-.255-1.065-.24-.45-.675-.705a2 2 0 0 0-1.02-.255q-.69 0-1.185.3-.48.285-.75.84-.255.54-.27 1.335 0 .81.255 1.38t.75.87 1.2.3 1.245-.3q.54-.315.915-.945l.975.615q-.54.87-1.365 1.32-.81.435-1.92.435m5.05-11.85h1.2v11.7h-1.2zm3.456 0h1.2v11.7h-1.2zm6.127 11.85q-.96 0-1.71-.45a3.24 3.24 0 0 1-1.154-1.275q-.405-.81-.406-1.875 0-1.08.42-1.89.435-.81 1.2-1.26.78-.45 1.8-.45t1.74.435q.736.42 1.126 1.215t.39 1.89q0 .105-.016.225v.135h-5.894v-1.02h4.95l-.48.69q.045-.075.09-.225.044-.165.044-.27 0-.615-.255-1.065-.24-.45-.675-.705a2 2 0 0 0-1.02-.255q-.69 0-1.184.3-.48.285-.75.84-.255.54-.27 1.335 0 .81.254 1.38.255.57.75.87t1.2.3q.706 0 1.246-.3.54-.315.915-.945l.975.615q-.54.87-1.365 1.32-.81.435-1.92.435m6.25-7.05v6.9h-1.2v-6.9zm2.085 1.32a1.9 1.9 0 0 0-.435-.255q-.195-.09-.495-.09-.45 0-.705.225t-.36.615q-.09.39-.09.885h-.54q0-.825.285-1.455.3-.645.765-1.02t.945-.375q.375 0 .69.105.33.09.6.375z'
            />
          </g>
          <defs>
            <clipPath id='clip0_13126_55493'>
              <path
                fill='#fff'
                d='M0 0h104v40H0z'
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    ) : null;

  const hasSpecialImage = sortedImages.some(validateSpecialImage);

  const handleScrollToSpecialImage = () => {
    const mobileScrollContainer = mobileScrollContainerRef.current;

    if (!mobileScrollContainerRef) {
      return;
    }

    mobileScrollContainer.scrollTo({
      left: mobileScrollContainer.scrollWidth - mobileScrollContainer.offsetWidth,
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.carouselContainer}>
        {isMobile ? (
          <>
            <div className=' overflow-hidden'>
              <div
                className='-mb-5 overflow-auto pb-5 pl-2.5 pt-2.5'
                ref={mobileScrollContainerRef}
              >
                <div className='flex items-center gap-x-2'>
                  {sortedImages.map((item, index) => {
                    return (
                      <div
                        className='w-[280px] flex-none'
                        key={index}
                      >
                        <div
                          style={{
                            paddingBottom: `${(1500 / 1022) * 100}%`,
                          }}
                          className='relative'
                        >
                          <Image
                            alt={generateImageAltText({
                              name,
                              index,
                              colorLabel,
                              fabricLabel,
                              categoryName,
                            })}
                            key={index}
                            src={generateFile({
                              item,
                              version,
                            })}
                            width={692}
                            height={1016}
                            onClick={() => onImageClick(index)}
                            className='absolute left-0 top-0 block h-full w-full object-contain object-center lg_object-top'
                            loader={({ src }) => src}
                          />
                          {index === 0 ? (
                            <>
                              {maybeNewMark}
                              {maybeBestSellerMark}
                            </>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            {videoUrl || hasSpecialImage ? (
              <div className='mt-2.5 px-2.5'>
                <MobileVideoPreview
                  onScrollToSpecialImage={handleScrollToSpecialImage}
                  hasSpecialImage={hasSpecialImage}
                  videoUrl={videoUrl}
                />
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className={videoUrl && showVideo ? '' : classes.magnifierContainer}>
              {videoUrl && showVideo ? (
                <AspectRatio ratio={1022 / 1500}>
                  <ReactPlayer
                    url={videoUrl}
                    loop={false}
                    controls
                    muted={false}
                    playbackRate={1}
                    width={'100%'}
                    height={'100%'}
                  />
                </AspectRatio>
              ) : (
                magnifier
              )}
            </div>
            {shareWith}
          </>
        )}

        {isMobile ? null : (
          <>
            {maybeBestSellerMark}
            {maybeNewMark}
          </>
        )}
      </div>
      {isMobile ? null : (
        <div
          className={clsx(
            'h-full  w-[116px] flex-none',
            'overflow-hidden',
            '[&_.slick-slider]_relative',
            '[&_.slick-slider_.slick-list]_overflow-hidden',
            '[&_.slick-slider_.slick-track]_flex',
            '[&_.slick-slider_.slick-track]_flex-col',
            '[&_.slick-arrow]_absolute',
            '[&_.slick-arrow.slick-disabled]_opacity-0',
            '[&_.slick-arrow.slick-disabled]_pointer-events-none',
            '[&_.slick-arrow]_w-full',
            '[&_.slick-arrow]_z-[5]',
            '[&_.slick-arrow]_left-0',
            '[&_.slick-prev]_top-0',
            '[&_.slick-next]_bottom-0',
            isImageGallery ? 'max-h-[648px]' : 'max-h-[1021px]',
          )}
        >
          <Slider
            vertical
            slidesToShow={isImageGallery ? 4 : 6.3}
            slidesToScroll={1}
            dots={false}
            arrows
            prevArrow={<SliderArrow />}
            nextArrow={<SliderArrow />}
            infinite={false}
            draggable={false}
          >
            {thumbnails}
          </Slider>
        </div>
      )}
      <Suspense fallback={null}>
        <ImagesViewerModal
          ref={imageViewerRef}
          images={productImages}
          index={currentProductImageIndex}
        />
      </Suspense>
    </div>
  );
};

const generateImageAltText = ({ name, index, colorLabel, fabricLabel, categoryName }) => {
  if (!name) {
    return '';
  }

  const nameParts = name.split(' ');

  if (categoryName) {
    nameParts.pop();
  }

  return [...nameParts, colorLabel, fabricLabel?.replace('-default', ''), categoryName, `image${index + 1}`]
    .filter(Boolean)
    .join(' ');
};

const generateYoutubeVideoPosterUrl = (videoUrl, defaultImageUrl) => {
  if (videoUrl.indexOf('https://youtu.be') !== 0 && videoUrl.indexOf('https://www.youtube.com') !== 0) {
    return defaultImageUrl;
  }

  const pathArray = videoUrl.split('/');
  const youtubeVideoId = pathArray[pathArray.length - 1];

  return `https://img.youtube.com/vi/${youtubeVideoId}/sddefault.jpg`;
};

const SliderArrow = ({ currentSlide, slideCount, children, ...props }) => {
  const isSlideNext = props.className?.includes('slick-next');

  return (
    <div {...props}>
      <div
        className={clsx(
          ' flex w-full cursor-pointer items-center justify-center bg-white/80 py-2.5',
          isSlideNext ? '' : 'rotate-180',
        )}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='20'
          height='11'
          fill='none'
          viewBox='0 0 20 11'
        >
          <g clipPath='url(#clip0_13050_3563)'>
            <path
              stroke='#4B5563'
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M1.563 1.563 10 10l8.438-8.437'
            />
          </g>
          <defs>
            <clipPath id='clip0_13050_3563'>
              <path
                fill='#fff'
                d='M.563 11V.562h18.875V11z'
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    </div>
  );
};

const validateSpecialImage = x => {
  const filenameParts = x?.file?.split('/')?.filter(Boolean) || [];

  if (!filenameParts.length) {
    return false;
  }

  const lastIndex = filenameParts.length - 1;

  return !!filenameParts[lastIndex]?.toLowerCase()?.includes('color-comparison');
};

export default ProductImageCarousel;
